// ForecastHeroBentoTile — Bento Hero (13W-Liquiditätsvorschau Konzern).
//
// Phase F.1 (Sprint 7 Final-Pass): Refactor zur prominenten Cashflow-Sicht.
// Big-Zahl = kumulierte Netto-Bewegung 13W (mode='delta') oder erwarteter
// Kontostand-Ende-13W (mode='kontostand', sobald Banking via EBICS aktiv).
// Darunter: ▼ Ausgaben gesamt / ▲ Eingänge erwartet, dann Sparkline 13W.
// Footer: gewichtete Genauigkeit (Phase F.4) statt pauschal ±18 %.

import { type Forecast13wKpi, type LiquiForecastResponse } from "../../../lib/api";
import { formatEurCompact } from "../../../lib/bwa-aggregate";
import { flashClass, useFlashOnChange } from "../../../hooks/useFlashOnChange";
import Sparkline from "../../charts/Sparkline";

interface Props {
  data?: Forecast13wKpi | null;
  /** Task 2 (2026-05-20): Aging-basierter Eingangs-Forecast (Konzern-Scope).
   * Wenn vorhanden, ueberschreibt die "Eingaenge erwartet"-Zeile + Sparkline-
   * Netto-Berechnung mit den Aging-Modell-Werten statt 0. */
  liquiData?: LiquiForecastResponse | null;
  loading?: boolean;
}

export default function ForecastHeroBentoTile({ data, liquiData, loading }: Props) {
  // K7-Kaskade: Endstand/Netto-Bewegung pulst 1× bei Änderung. Hook VOR den
  // Early-Returns (Rules of Hooks), Wert self-contained berechnet.
  const forecastFlash = useFlashOnChange(
    data && (data.series?.length ?? 0) > 0
      ? data.mode === "kontostand"
        ? Number(data.end_saldo_eur ?? "0")
        : (liquiData && Number(liquiData.summary.total_eingang_eur) > 0
            ? Number(liquiData.summary.total_eingang_eur)
            : Number(data.total_zufluesse_eur ?? "0")) -
          Number(data.total_abfluesse_eur ?? "0")
      : null,
  );

  if (loading) return <Skeleton />;

  const series = data?.series ?? [];
  if (!data || series.length === 0) {
    return (
      <div className="h-full flex flex-col justify-between gap-2">
        <header className="text-[11px] uppercase tracking-widest opacity-60">
          13-Wochen-Liquiditätsvorschau
        </header>
        <div className="text-xs text-slate-500">
          Forecast wird berechnet — Steuer-Termine + Recurring-Patterns laden
        </div>
      </div>
    );
  }

  const isKontostand = data.mode === "kontostand";
  // Task 2: Aging-Modell-Eingang ueberschreibt den 0-Default, wenn liquiData da.
  const liquiEingangTotal = liquiData
    ? Number(liquiData.summary.total_eingang_eur)
    : 0;
  const totalZufluesseRaw = Number(data.total_zufluesse_eur ?? "0");
  const totalZufluesse = liquiEingangTotal > 0 ? liquiEingangTotal : totalZufluesseRaw;
  const totalAbfluesse = Number(data.total_abfluesse_eur ?? "0");
  const bigValueEur = isKontostand
    ? Number(data.end_saldo_eur ?? "0")
    : totalZufluesse - totalAbfluesse;
  const confidencePct = data.confidence_band_pct ?? 18;
  const lineSeries = series.map((s) => s.p50 ?? s.end_saldo);

  // Severity: Kontostand-Modus rot wenn End-Saldo negativ. Delta-Modus
  // rot wenn Netto-Bewegung deutlich negativ (Ausgaben dominieren) — bei
  // <-1.000 € werten wir als kritisch.
  const severity: "ok" | "critical" =
    isKontostand && bigValueEur < 0
      ? "critical"
      : !isKontostand && bigValueEur < -1000
        ? "critical"
        : "ok";

  const bigSign = bigValueEur < 0 ? "" : isKontostand ? "" : "+";

  return (
    <div className="h-full flex flex-col justify-between gap-2">
      <header className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-widest opacity-60">
          13-Wochen-Liquiditätsvorschau
        </span>
        <span
          className="text-[10px] tabular-nums px-2 py-0.5 rounded border border-slate-600/40 bg-slate-700/20 text-slate-300"
          title={
            isKontostand
              ? "Erwarteter Kontostand am Ende der 13 Wochen — Startwert ist der konsolidierte Bank-Saldo (manueller Eintrag oder EBICS)"
              : "Kumulierte Netto-Bewegung 13W (Aging-Forderungs-Modell). Schaltet auf Kontostand-Sicht, sobald ein Bank-Saldo vorliegt (manueller Eintrag oder EBICS)."
          }
        >
          {isKontostand ? "Kontostand 13W" : "Netto-Bewegung 13W"}
        </span>
      </header>

      <div className="flex-1 flex flex-col justify-center gap-2">
        <div
          className={`hero-number ${severity === "critical" ? "hero-number-red" : ""} ${flashClass(forecastFlash)}`}
        >
          {bigSign}
          {formatEurCompact(bigValueEur)}
        </div>

        {/* Restraint-Pass (Auftrag H Schritt 5): Konzern-Chart monochrom —
            Pfeile tragen die Richtung, Farbe bleibt dem Alarm vorbehalten. */}
        <ul className="text-[11px] space-y-0.5 text-slate-300">
          <li className="flex items-center justify-between gap-2 tabular-nums">
            <span>▼ Ausgaben gesamt</span>
            <span>−{formatEurCompact(totalAbfluesse)}</span>
          </li>
          <li className="flex items-center justify-between gap-2 tabular-nums">
            <span>▲ Eingänge erwartet</span>
            <span>
              {totalZufluesse > 0 ? "+" : ""}
              {formatEurCompact(totalZufluesse)}
            </span>
          </li>
        </ul>

        {!isKontostand && (
          <p
            className="text-[10px] text-slate-500 leading-tight"
            title={
              liquiData?.annahmen
                ? `Aging-Modell: P_0-30=${(liquiData.annahmen.P_0_30 * 100).toFixed(0)}%, ` +
                  `P_30-60=${(liquiData.annahmen.P_30_60 * 100).toFixed(0)}%, ` +
                  `P_60-90=${(liquiData.annahmen.P_60_90 * 100).toFixed(0)}%, ` +
                  `P_90+=${(liquiData.annahmen.P_90_plus * 100).toFixed(0)}%`
                : undefined
            }
          >
            Startwert wartet auf EBICS. Forecast aus Aging-Modell
            {liquiData?.annahmen
              ? ` (P_0-30=${(liquiData.annahmen.P_0_30 * 100).toFixed(0)}%, ` +
                `P_30-60=${(liquiData.annahmen.P_30_60 * 100).toFixed(0)}%, ` +
                `P_60-90=${(liquiData.annahmen.P_60_90 * 100).toFixed(0)}%, ` +
                `P_90+=${(liquiData.annahmen.P_90_plus * 100).toFixed(0)}%)`
              : ""}
          </p>
        )}

        {data.next_tax_marker && (
          <p
            className="text-[10px] text-amber-200 truncate"
            title={data.next_tax_marker.headline}
          >
            ⚡ {data.next_tax_marker.headline}
          </p>
        )}
      </div>

      <div className="-mx-1">
        {/* Monochrom (dim) im Normalfall, Clay nur im Alarm (§4.5). */}
        <Sparkline
          data={lineSeries}
          hex={severity === "critical" ? "#d97757" : "#a8a29a"}
          height={28}
          showLast
          ariaLabel={`13W-Liquiditätsvorschau ${lineSeries.length} Wochen`}
        />
      </div>

      <p className="text-[10px] text-slate-500 leading-tight">
        Genauigkeit ±{confidencePct.toFixed(0)} % — verbessert sich mit längerer
        Historie
      </p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="h-full flex flex-col justify-between gap-2 animate-pulse">
      <div className="h-3 w-1/3 rounded bg-slate-700/50" />
      <div className="h-10 w-2/3 rounded bg-slate-700/40" />
      <div className="h-7 w-full rounded bg-slate-700/20" />
    </div>
  );
}
